import styled from "styled-components";
import {
  getBorderRadius,
  getColor,
  getFadeInAnimation,
  getSize,
} from "~/helpers/style";
import { sidebarWidth } from "~/components/layout/Sidebar.style";

/* ── Sticky Banner (bottom-left, dark card) ──────────────────── */

export const BannerContainer = styled.div`
  position: fixed;
  bottom: ${getSize(2.4)};
  left: calc(${sidebarWidth}px + ${getSize(2.4)});
  z-index: 9;
  width: ${getSize(32)};
  background: #2d2d3d;
  border-radius: ${getBorderRadius(1.2)};
  padding: ${getSize(2)} ${getSize(2)};
  color: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  ${getFadeInAnimation(0.3)}

  @media (max-width: 768px) {
    left: ${getSize(2.4)};
  }
`;

export const BannerCloseButton = styled.button.attrs<{ disabled?: boolean }>(
  (props) => ({ disabled: props.disabled })
)`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  color: ${({ disabled }) => (disabled ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.7)")};
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  font-size: 14px;
  line-height: 1;
  padding: 4px;

  &:hover {
    color: ${({ disabled }) => (disabled ? "rgba(255,255,255,0.2)" : "#fff")};
  }
`;

export const BannerTitle = styled.h4`
  margin: 0 0 ${getSize(0.6)};
  font-size: ${getSize(1.4)};
  font-weight: 700;
  color: #fff;
  padding-right: ${getSize(2)};
`;

export const BannerDescription = styled.p`
  margin: 0 0 ${getSize(1.6)};
  font-size: ${getSize(1.2)};
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
`;

export const ClaimButton = styled.button`
  width: 100%;
  padding: ${getSize(1)} ${getSize(2)};
  background: #fff;
  color: #2d2d3d;
  border: none;
  border-radius: ${getBorderRadius(0.6)};
  font-size: ${getSize(1.3)};
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f0f0f0;
  }
`;

/* ── Modal Overlay ───────────────────────────────────────────── */

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  padding: ${getSize(2)};
  ${getFadeInAnimation(0.2)}
`;

/* ── Confirmation Modal ──────────────────────────────────────── */

export const ConfirmModal = styled.div`
  position: relative;
  background: #fff;
  border-radius: ${getBorderRadius(1.2)};
  padding: ${getSize(3.2)};
  width: 100%;
  max-width: ${getSize(52)};
  display: flex;
  flex-direction: column;
`;

export const ConfirmCloseButton = styled.button`
  position: absolute;
  top: ${getSize(1.6)};
  right: ${getSize(1.6)};
  background: none;
  border: none;
  color: ${getColor("secondary")};
  cursor: pointer;
  font-size: ${getSize(1.6)};
  line-height: 1;

  &:hover {
    color: ${getColor("text")};
  }
`;

export const ConfirmTitle = styled.h2`
  margin: 0 0 ${getSize(1)};
  font-size: ${getSize(1.8)};
  font-weight: 700;
  color: ${getColor("text")};
`;

export const ConfirmDescription = styled.p`
  margin: 0 0 ${getSize(2)};
  font-size: ${getSize(1.4)};
  color: ${getColor("secondary")};
  line-height: 1.6;
`;

export const TestimonialRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSize(1.2)};
  background: #f8f8f8;
  border-radius: ${getBorderRadius(0.8)};
  padding: ${getSize(1.2)} ${getSize(1.6)};
  margin-bottom: ${getSize(2.4)};
`;

export const TestimonialAvatar = styled.div`
  width: ${getSize(3.6)};
  height: ${getSize(3.6)};
  border-radius: 50%;
  background: #ddd;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const TestimonialText = styled.p`
  margin: 0;
  font-size: ${getSize(1.3)};
  font-style: italic;
  color: ${getColor("secondary")};
  line-height: 1.5;
`;

export const CardSection = styled.div`
  border: 1px solid ${getColor("disabled")};
  border-radius: ${getBorderRadius(0.8)};
  padding: ${getSize(1.4)} ${getSize(1.6)};
  margin-bottom: ${getSize(2.4)};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${getSize(0.2)};
`;

export const CardLabel = styled.span`
  font-size: ${getSize(1.1)};
  color: ${getColor("secondary")};
`;

export const CardNumber = styled.span`
  font-size: ${getSize(1.4)};
  font-weight: 500;
  color: ${getColor("text")};
`;

export const ChangeCardLink = styled.button`
  background: none;
  border: none;
  color: ${getColor("primary")};
  font-size: ${getSize(1.3)};
  font-weight: 500;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

export const GetDiscountButton = styled.button`
  width: 100%;
  padding: ${getSize(1.4)} ${getSize(2)};
  background: ${getColor("primary")};
  color: #fff;
  border: none;
  border-radius: ${getBorderRadius(0.6)};
  font-size: ${getSize(1.5)};
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s;

  &:hover {
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
